import { Form, message, Modal, Radio, Select } from "antd";

import { useForm } from "antd/lib/form/Form";
import { formatError } from "common/redux/middleware/queryErrorLogger";
import { AktDateRangePicker } from "components/aktDatePicker";
import { useFetchLetterTemplatesQuery } from "features/letterTemplates/letterTemplatesAPI";
import moment from "moment-timezone";
import styled from "styled-components";
import { downloadFileFromUrl } from "common/utils";
import { useFetchCommunicationMethodsQuery } from "features/communicationMethodsTable/communicationMethodsAPI";
import { useCommunicationMethodPermissions } from "features/communicationMethodsTable/hooks/useCommunicationMethodPermissions";
import { useDownloadInHouseLettersMutation } from "../documentCenterAPI";

const StyledForm = styled(Form)`
  margin-top: 12px;
  max-width: 350px;
`;

function DownloadInHouseLetterModal({
  open,
  onOk,
  onCancel,
  accountIds = null,
  useAsyncJob = true,
}) {
  const [form] = useForm();
  const [downloadInHouseLetters, { isLoading: isInHouseLettersDownloading }] =
    useDownloadInHouseLettersMutation();
  const { data: communicationMethods = [], isLoading: isLoadingCommunicationMethods } =
    useFetchCommunicationMethodsQuery();
  const { filterAllowedCommunicationMethods } = useCommunicationMethodPermissions();

  const communicationMethodInHouseLetterTemplateIds = new Set(
    filterAllowedCommunicationMethods(communicationMethods)
      ?.filter((commMethod) => !commMethod.isArchived)
      .flatMap((commMethod) =>
        commMethod.medium === "in_house_letter" ? commMethod.letterTemplateIds : [],
      ),
  );

  const { data: letterTemplates = [], isLoading: isLoadingLetterTemplates } =
    useFetchLetterTemplatesQuery();

  const isLoading = isLoadingCommunicationMethods && isLoadingLetterTemplates;

  const letterTemplateOptions = letterTemplates
    .filter((template) => communicationMethodInHouseLetterTemplateIds.has(template.id))
    .map((template) => ({
      value: template.id,
      label: template.filename,
    }));

  const filterOption = (inputValue, option) => {
    const fullOptionText = option.label;
    return fullOptionText?.toLowerCase().includes(inputValue?.toLowerCase());
  };

  const onSubmit = async () => {
    const values = await form.validateFields();
    const { dateRange, letterTemplateIds, renderFormat, status, useCombinedDocument } = values;
    const [startDate, endDate] = dateRange;

    // Map status to statuses parameter
    let statuses = null;
    if (status === "not_downloaded") {
      statuses = ["scheduled"];
    } else if (status === "downloaded") {
      statuses = ["success"];
    }
    // For "all", statuses remains null

    const result = await downloadInHouseLetters({
      letterTemplateIds,
      startDate,
      endDate,
      renderFormat,
      statuses,
      useAsyncJob,
      useCombinedDocument,
      ...(accountIds !== null ? { accountIds } : {}),
    });
    if ("data" in result) {
      await onOk();
      form.resetFields();
      if (useAsyncJob) {
        return message.success(
          "In-House letters file is being generated. Please check back later in the Generated Documents section.",
        );
      }
      downloadFileFromUrl(result.data.fileUrl, result.data.filename);
      return message.success("Starting to download In-House letters");
    }
    if ("error" in result) {
      form.setFields(formatError(result.error));
    }
  };

  const disabledAfterTodayDate = (current) => {
    const today = moment().endOf("day");
    return current && current > today;
  };

  const initialValues = {
    letterTemplateIds: letterTemplateOptions.map((template) => template.value),
    renderFormat: "pdf",
    // Default to "not_downloaded" which maps to statuses: ["scheduled"]
    status: "not_downloaded",
  };

  return (
    <Modal
      maskClosable={false}
      title="Download In-House Letters"
      open={open}
      onOk={onSubmit}
      onCancel={onCancel}
      okButtonProps={{
        loading: isLoading,
      }}
      confirmLoading={isInHouseLettersDownloading}
    >
      <StyledForm layout="vertical" form={form} initialValues={initialValues}>
        <Form.Item
          label="Letter Template"
          name="letterTemplateIds"
          rules={[{ required: true, message: "Select letter template" }]}
        >
          <Select
            showSearch
            popupMatchSelectWidth={false}
            allowClear
            mode="multiple"
            disabled={isLoading}
            options={letterTemplateOptions}
            filterOption={filterOption}
          />
        </Form.Item>
        <Form.Item
          name="dateRange"
          label="Date Range"
          rules={[{ required: true, message: "Select date range" }]}
        >
          <AktDateRangePicker disabledDate={disabledAfterTodayDate} picker="date" allowClear />
        </Form.Item>
        <Form.Item
          label="File Format"
          name="renderFormat"
          rules={[{ required: true, message: "This field is required" }]}
        >
          <Select
            placeholder="Select one..."
            options={[
              {
                label: "PDF",
                value: "pdf",
              },
              {
                label: "Docx",
                value: "docx",
              },
            ]}
          />
        </Form.Item>
        <Form.Item
          label="Status"
          name="status"
          rules={[{ required: true, message: "Please select a status" }]}
        >
          <Select
            placeholder="Select status..."
            options={[
              {
                label: "Not Downloaded",
                value: "not_downloaded", // Maps to statuses: ["scheduled"]
              },
              {
                label: "Downloaded",
                value: "downloaded", // Maps to statuses: ["success"]
              },
              {
                label: "All",
                value: "all", // Maps to statuses: null
              },
            ]}
          />
        </Form.Item>
        <Form.Item
          noStyle
          shouldUpdate={(prevValues, currentValues) =>
            prevValues.renderFormat !== currentValues.renderFormat
          }
        >
          {({ getFieldValue }) => {
            const format = getFieldValue("renderFormat");
            return (
              <Form.Item name="useCombinedDocument" initialValue={false} label="Output As">
                <Radio.Group>
                  {format === "pdf" && <Radio value>Combined in a PDF</Radio>}
                  <Radio value={false}>Separate in a ZIP</Radio>
                </Radio.Group>
              </Form.Item>
            );
          }}
        </Form.Item>
      </StyledForm>
    </Modal>
  );
}

export default DownloadInHouseLetterModal;
